/**
 * V11.8 — Admin : fiches créées automatiquement par l'IA (Curator).
 * Réservé aux rôles admin/COPF. Permet d'identifier les fiches à enrichir.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Bot, Pencil, ExternalLink, Users, CheckCircle2, Clock, Trophy } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";

export default function AdminAIAthletesPage() {
  const { user } = useAuth();
  const isStaff = (user?.roles || []).some((r) => ["admin", "copf"].includes(r));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("unenriched");

  useEffect(() => {
    if (!isStaff) return;
    setLoading(true);
    const enriched = tab === "enriched" ? true : tab === "unenriched" ? false : undefined;
    api.get("/home/ai-created-athletes", { params: { limit: 200, ...(enriched !== undefined ? { enriched } : {}) } })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); showError(e); })
      .finally(() => setLoading(false));
  }, [isStaff, tab]);

  if (!isStaff) {
    return <div className="mx-auto max-w-3xl px-4 py-16 text-center">
      <Bot className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
      <p>Accès réservé aux rôles Admin/COPF.</p>
    </div>;
  }

  const items = data?.items || [];

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-ai-athletes-page">
      <header className="mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Bot className="h-7 w-7 text-violet-500" />
              Fiches créées par IA
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Fiches créées automatiquement à partir d'articles du Curator (détection de noms d'athlètes cités).
              À enrichir en priorité pour les publier.
            </p>
          </div>
          {data && (
            <div className="flex gap-2 text-xs">
              <Badge variant="outline" className="gap-1 px-2 py-1"><Trophy className="h-3 w-3" /> {data.total_count} au total</Badge>
              <Badge variant="secondary" className="gap-1 px-2 py-1 bg-amber-100 text-amber-800 border-amber-300"><Clock className="h-3 w-3" /> {data.unenriched_count} à enrichir</Badge>
            </div>
          )}
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="admin-ai-athletes-tabs">
          <TabsTrigger value="unenriched" data-testid="tab-unenriched">À enrichir</TabsTrigger>
          <TabsTrigger value="enriched" data-testid="tab-enriched">Enrichies (publiées)</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">Toutes</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-16" /><Skeleton className="h-16" /><Skeleton className="h-16" />
            </div>
          ) : items.length === 0 ? (
            <Card className="p-8 text-center text-muted-foreground">
              <Bot className="h-8 w-8 mx-auto mb-2 opacity-50" />
              Aucune fiche créée par IA {tab === "unenriched" && "à enrichir"} pour l'instant.
            </Card>
          ) : (
            <Card className="divide-y" data-testid="admin-ai-athletes-list">
              {items.map((a) => (
                <div key={a.id} className="flex items-center gap-3 p-3" data-testid={`ai-athlete-${a.id}`}>
                  {a.photo_url ? (
                    <img src={a.photo_url} alt={a.full_name} className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                      <Users className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/athletes/${a.slug}`} className="font-medium text-sm hover:underline" data-testid={`ai-athlete-link-${a.id}`}>
                        {a.full_name}
                      </Link>
                      {a.published ? (
                        <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-700">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Publié
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-700">
                          <Clock className="h-3 w-3 mr-1" /> Brouillon
                        </Badge>
                      )}
                      {a.main_discipline && (
                        <Badge variant="secondary" className="text-[10px] capitalize">{a.main_discipline.replace(/-/g, " ")}</Badge>
                      )}
                      <Badge className="text-[10px] bg-violet-500 text-white gap-1">
                        <Bot className="h-3 w-3" /> IA
                      </Badge>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {a.achievements_count} résultat{a.achievements_count !== 1 ? "s" : ""} ·
                      {" "}Créé le {a.created_at?.slice(0, 10)}
                      {a.curator_item_title && (
                        <span className="italic ml-1">· depuis « {a.curator_item_title} »</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Link to={`/athletes/${a.id}/edit`}>
                      <Button size="sm" variant="outline" data-testid={`ai-athlete-edit-${a.id}`}>
                        <Pencil className="h-3 w-3 mr-1" /> Enrichir
                      </Button>
                    </Link>
                    <Link to={`/athletes/${a.slug}`}>
                      <Button size="sm" variant="ghost" data-testid={`ai-athlete-view-${a.id}`}>
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
